import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Anchor } from "lucide-react";

export default function MFAEnforcementGate() {
  const navigate = useNavigate();
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="mfa-enforcement-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfa-gate-title"
    >
      {/* Solid + grain backdrop, NOT dismissible */}
      <div
        className="absolute inset-0 bg-[#040404]/95 backdrop-blur-md"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1752350434868-af7431a9f14b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNyaW1zb24lMjByZWQlMjBwYXJ0aWNsZXxlbnwwfHx8fDE3Nzc3MTA2Mzh8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundBlendMode: "multiply",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#040404]/85 via-[#040404]/95 to-[#040404]" />

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative w-full max-w-xl glass-panel-strong angled-corner grain p-10"
      >
        {/* Crimson alert strip */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#C1432E] to-transparent" />

        <div className="flex items-center gap-3 mb-6">
          <motion.div
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="h-12 w-12 grid place-items-center border border-[#C1432E] bg-[#C1432E]/10 angled-corner"
          >
            <ShieldAlert size={20} className="text-[#E55A45]" />
          </motion.div>
          <div>
            <div className="label-mono text-[#E55A45]" data-testid="mfa-gate-eyebrow">
              SOVEREIGN ALERT · CRITICAL
            </div>
            <h2 id="mfa-gate-title" className="font-display text-2xl mt-1">
              Security Hardening Required
            </h2>
          </div>
        </div>

        <div className="gold-divider mb-6" />

        <p className="text-zinc-200 leading-relaxed text-[15px]" data-testid="mfa-gate-warning">
          <span className="text-[#E55A45] font-display tracking-tight text-base">
            YOUR VESSEL IS AT RISK.
          </span>{" "}
          Fleet protocols require <span className="text-[#D8AB4E]">mandatory Multi-Factor Authentication</span>{" "}
          to maintain Command Access. Until enrolment is verified, the bridge will continue to flag this seafarer as exposed.
        </p>

        <ul className="mt-6 space-y-2 text-sm text-zinc-400">
          <li className="flex items-start gap-3">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D8AB4E] shrink-0" />
            <span>Applies to <span className="text-zinc-200">every account class</span> — Director, Commander, Seafarer.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D8AB4E] shrink-0" />
            <span>Bind any TOTP authenticator (Google Authenticator, 1Password, Authy).</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D8AB4E] shrink-0" />
            <span>Setup takes under 60 seconds. Backup recovery codes issued automatically.</span>
          </li>
        </ul>

        <div className="mt-8 flex items-center gap-3">
          <button
            data-testid="mfa-gate-setup-button"
            onClick={() => navigate("/mfa/setup")}
            className="btn-gold flex-1 py-3.5 text-[12px] tracking-[0.22em]"
          >
            SET UP MFA NOW
          </button>
        </div>

        <div className="mt-6 flex items-center gap-2 text-[10px] font-mono-data tracking-[0.22em] text-zinc-500">
          <Anchor size={11} className="text-[#D8AB4E]" />
          THIS PROMPT WILL PERSIST UNTIL ENROLMENT IS COMPLETE.
        </div>
      </motion.div>
    </div>
  );
}
